//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';

/**
 * One thing a user can open: the chat panel, the demo-data loader, or any
 * surface a plugin offers.
 *
 * Heterogeneous on purpose. The taskbar and the Tools list both draw from
 * this one list, so a plugin panel, a modal and a menu-opening action are
 * pinned, ordered and rendered by the same code rather than by three special
 * cases.
 */
export interface ToolEntry {
  readonly id: string;
  // Short name, for the taskbar pill where width is scarce.
  readonly label: string;
  // Full name, for the Tools row where it is not.
  readonly name: string;
  readonly icon: React.ReactNode;
  // True when the glyph above is one several tools share rather than this
  // tool's own -- the plugin fallback. Carried so genericity survives into
  // the entry for a launcher to consult: the panel pill does, and draws no
  // glyph at all for one; the other launchers draw it regardless.
  readonly isIconGeneric?: boolean;
  // Set only when the tool is a panel. Its lifecycle drives the pill's
  // enclosure; a tool without one has no running state and stays ghost.
  readonly panelId?: string;
  // The element activated is passed through, for a tool that anchors a menu
  // of its own to whatever the user clicked.
  readonly open: (anchor: HTMLElement) => void;
  readonly isDisabled?: boolean;
  readonly disabledReason?: string;
  // Set when `open` reveals a menu rather than acting outright, with whether
  // that menu is open now. Every launcher drawn from this entry announces it
  // (`aria-haspopup`/`aria-expanded`), so what a screen reader says before
  // activation matches what activation does.
  readonly popup?: 'menu';
  readonly isExpanded?: boolean;
}

// The one thing every launcher says about a panel beyond its name and its
// disabled reason: that it is running somewhere the user cannot currently
// see. An open panel needs no such line -- it already answers by being on
// screen -- so the pill, the taskbar's flyout and the Tools list all draw
// this from here, and a tool reads the same wherever its state is shown.
export const runningDescription = (
  name: string, isMounted: boolean, isOpen: boolean
): string | undefined =>
  isMounted && !isOpen ? `${name} — running in background` : undefined;
